import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { catchAll } from '@sorare/routing';

import type { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  ANY_SPORT_PLAY_EVENTTYPE_ARENA_FIXTURE_LINEUPS,
  ANY_SPORT_PLAY_EVENTTYPE_CHAMPIONSHIP,
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS,
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_REWARDS,
  ANY_SPORT_PLAY_QUICK_PLAY,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useSimplifiedNav } from '@sorare/core/src/hooks/commonGame/useSimplifiedNav';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { glossary, navLabels } from '@sorare/core/src/lib/glossary';

type Props = {
  so5FixtureSlug?: string;
  eventType: So5FixtureEvent;
  children: ReactNode;
};

export const FixtureTabLayout = ({
  so5FixtureSlug,
  eventType,
  children,
}: Props): ReactNode => {
  const {
    flags: {
      enableChampionshipPerSport = {
        football: false,
        nba: false,
        baseball: false,
      },
      useQuickPlay = {
        [Sport.BASEBALL]: false,
        [Sport.NBA]: false,
        [Sport.FOOTBALL]: false,
      },
      enableArenaV2 = false,
      useKillRewardTab = false,
    },
  } = useFeatureFlags();

  const { sport, generateSportContextPath } = useSportContext();
  const { showSimplifiedNav } = useSimplifiedNav(sport);

  if (!so5FixtureSlug) {
    return children;
  }

  return (
    <>
      <PageHeader text={navLabels.play} disableBackBrowser />
      <ContentWithStickyHeader
        header={
          <FullWidthContainer.NoPadding>
            <TabBar variant="flat" fullWidth>
              {!!useQuickPlay[sport] && (
                <Tab
                  to={generateSportContextPath(ANY_SPORT_PLAY_QUICK_PLAY)}
                  matches={[
                    catchAll(
                      generateSportContextPath(ANY_SPORT_PLAY_QUICK_PLAY)
                    ),
                  ]}
                >
                  <FormattedMessage {...glossary.quickPlay} />
                </Tab>
              )}

              <Tab
                to={generateSportContextPath(
                  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS,
                  {
                    params: {
                      fixture: so5FixtureSlug,
                      eventType: eventType.toLowerCase(),
                    },
                  }
                )}
              >
                <FormattedMessage {...navLabels.pro} />
              </Tab>
              {!!enableArenaV2 && (
                <Tab
                  to={generateSportContextPath(
                    ANY_SPORT_PLAY_EVENTTYPE_ARENA_FIXTURE_LINEUPS,
                    {
                      params: {
                        fixture: so5FixtureSlug,
                        eventType: eventType.toLowerCase(),
                      },
                    }
                  )}
                >
                  <FormattedMessage
                    id="FixtureTabLayout.arena"
                    defaultMessage="Arena"
                  />
                </Tab>
              )}
              {!useKillRewardTab && (
                <Tab
                  to={generateSportContextPath(
                    ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_REWARDS,
                    {
                      params: {
                        fixture: so5FixtureSlug,
                        eventType: eventType.toLowerCase(),
                      },
                    }
                  )}
                >
                  <FormattedMessage {...glossary.rewards} />
                </Tab>
              )}
              {!!enableChampionshipPerSport[sport.toLowerCase()] &&
                !showSimplifiedNav && (
                  <Tab
                    matches={[
                      catchAll(
                        generateSportContextPath(
                          ANY_SPORT_PLAY_EVENTTYPE_CHAMPIONSHIP,
                          {
                            params: {
                              eventType: eventType.toLowerCase(),
                            },
                          }
                        )
                      ),
                    ]}
                    to={generateSportContextPath(
                      ANY_SPORT_PLAY_EVENTTYPE_CHAMPIONSHIP,
                      {
                        params: {
                          eventType,
                        },
                      }
                    )}
                  >
                    <FormattedMessage
                      id="FixtureTabLayout.championship"
                      defaultMessage="Championship"
                    />
                  </Tab>
                )}
            </TabBar>
          </FullWidthContainer.NoPadding>
        }
      >
        {children}
      </ContentWithStickyHeader>
    </>
  );
};
